/**
 * Utility functions for View Transitions API
 * https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
 */

/**
 * Checks if the View Transitions API is supported in the current browser.
 */
export function supportsViewTransitions(): boolean {
  return typeof document !== 'undefined' && 'startViewTransition' in document;
}

/**
 * Executes a callback within a View Transition, or falls back to immediate execution
 * if View Transitions API is not supported.
 *
 * @param callback - The DOM update callback to execute
 * @returns Promise that resolves when the transition completes
 *
 * @example
 * ```typescript
 * await withViewTransition(() => {
 *   this.isOpen.set(false);
 * });
 * ```
 */
export async function withViewTransition(
  callback: () => void | Promise<void>
): Promise<void> {
  if (!supportsViewTransitions()) {
    await callback();
    return;
  }

  const transition = document.startViewTransition(callback);
  await transition.finished;
}

/**
 * Executes a callback within a View Transition, returning the transition object
 * for more control over the animation lifecycle.
 *
 * @param callback - The DOM update callback to execute
 * @returns The ViewTransition object or null if not supported
 *
 * @example
 * ```typescript
 * const transition = startViewTransition(() => {
 *   this.isOpen.set(false);
 * });
 *
 * if (transition) {
 *   await transition.finished;
 * }
 * ```
 */
export function startViewTransition(
  callback: () => void | Promise<void>
): ViewTransition | null {
  if (!supportsViewTransitions()) {
    callback();
    return null;
  }

  return document.startViewTransition(callback);
}

/**
 * Sets the view-transition-name CSS property on an element.
 * This is useful for dynamically assigning transition names.
 *
 * @param element - The HTML element to set the transition name on
 * @param name - The transition name (e.g., 'dialog', 'sidebar')
 */
export function setViewTransitionName(
  element: HTMLElement,
  name: string | null
): void {
  if (name) {
    element.style.setProperty('view-transition-name', name);
  } else {
    element.style.removeProperty('view-transition-name');
  }
}

/**
 * Creates a scoped view transition that only affects elements with specific
 * view-transition-name values.
 *
 * @param element - The element to scope the transition to
 * @param transitionName - The name for the view transition
 * @param callback - The DOM update callback
 */
export async function withScopedViewTransition(
  element: HTMLElement,
  transitionName: string,
  callback: () => void | Promise<void>
): Promise<void> {
  setViewTransitionName(element, transitionName);

  await withViewTransition(callback);

  setViewTransitionName(element, null);
}
